import React, { useEffect, useState, createContext } from "react";
import { ToastContainer } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

import "./App.css";
import "./assets/scss/app-modern.scss";
import "react-toastify/dist/ReactToastify.css";

import HorizontalNav from "./components/Horizontal-Nav";
import TestEnvironmentBanner from "./components/TestEnvironmentBanner";
import Footer from "./components/Footer";
import Routing from "./routing/index";
import { AppInitialization } from "./services/firebase";
import {
  registerAppNavigate,
  unregisterAppNavigate,
  appNavigate,
} from "./services/navigationService";
import type { AppSessionUser } from "./types/session";
import { SaccoListProvider } from "./contexts/SaccoListContext";
import { normalizePath } from "./services/rbacService";
import {
  hasSaccoMemberProfile,
  isPublicAuthPath,
  resolvePostLoginPath,
  shouldRedirectFromRoot,
  SACCO_MEMBER_ONBOARDING_PATH,
} from "./services/sessionAccess";
import { registerSaccoUserPatcher } from "./services/sessionRefresh";
import HelpCenterButton from "./components/HelpCenterButton";

export const UserContext = createContext<AppSessionUser | undefined>(undefined);
export const AuthContext = createContext<unknown>(undefined);

function NavigationBinding() {
  const navigate = useNavigate();
  useEffect(() => {
    registerAppNavigate(navigate);
    return () => unregisterAppNavigate();
  }, [navigate]);
  return null;
}

function App() {
  const [auth, setAuth] = useState<boolean | undefined>(undefined);
  const [user, setUser] = useState<AppSessionUser | undefined>(undefined);

  const _location = useLocation();
  const pathname = normalizePath(_location.pathname);
  const NoNavPages = [
    "/login",
    "/register",
    "/sacco/account/register",
  ];
  const _yesNav = !NoNavPages.includes(pathname);

  useEffect(() => {
    registerSaccoUserPatcher((saccoUser) => {
      setUser((prev) =>
        prev?.currentUser ? { ...prev, saccoUser, authResolved: true } : prev,
      );
    });
    return () => registerSaccoUserPatcher(null);
  }, []);

  useEffect(() => {
    if (user == null || !user.authResolved) {
      return;
    }

    if (!user.currentUser) {
      setAuth(false);
      if (!isPublicAuthPath(pathname)) {
        appNavigate("/login", { replace: true });
      }
      return;
    }

    setAuth(true);

    if (!hasSaccoMemberProfile(user.saccoUser)) {
      if (pathname !== SACCO_MEMBER_ONBOARDING_PATH) {
        appNavigate(SACCO_MEMBER_ONBOARDING_PATH, { replace: true });
      }
      return;
    }

    if (
      isPublicAuthPath(pathname) ||
      pathname === SACCO_MEMBER_ONBOARDING_PATH ||
      shouldRedirectFromRoot(user.saccoUser, pathname)
    ) {
      appNavigate(resolvePostLoginPath(user.saccoUser), { replace: true });
    }
  }, [user, pathname]);

  useEffect(() => {
    const unsubscribe = AppInitialization(setUser);
    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <div className="App">
      <NavigationBinding />
      <UserContext.Provider value={user}>
        {React.createElement(SaccoListProvider, {
          user,
          children: (
            <>
              {_yesNav ? <HorizontalNav /> : null}
              {process.env.REACT_APP_IS_STAGING === "true" && (
                <TestEnvironmentBanner />
              )}
              <div className={_yesNav ? "wrapper" : ""}>
                <div className={_yesNav ? "content-page" : ""}>
                  <div className={_yesNav ? "content" : ""}>
                    <Routing auth={auth} />
                  </div>
                </div>
              </div>
              <Footer />
              <HelpCenterButton />
              <ToastContainer theme="colored" position="top-center" />
            </>
          ),
        })}
      </UserContext.Provider>
    </div>
  );
}

export default App;
